// src/pages/admin/AdminUserManagementPage.jsx
import { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Typography, TextField, Button, IconButton,
  Switch, FormControlLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, Alert, Chip, CircularProgress, Avatar, Snackbar,
  Divider, LinearProgress, Select, MenuItem, InputLabel, FormControl,
  Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import EditIcon        from "@mui/icons-material/Edit";
import DeleteIcon      from "@mui/icons-material/Delete";
import RefreshIcon     from "@mui/icons-material/Refresh";
import AddIcon         from "@mui/icons-material/Add";
import LockResetIcon   from "@mui/icons-material/LockReset";
import SearchIcon      from "@mui/icons-material/Search";
import CreditCardIcon  from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { pageWrap, tableHeaderCell, tableRow, btnPrimary } from "../../utils/pageStyles";

// Dialog creation/edition
function UserFormDialog({ open, onClose, onSubmit, editingUser }) {
  const { t }  = useTranslation();
  const isEdit = Boolean(editingUser);
  const [username, setUsername] = useState("");
  const [email,    setEmail]    = useState("");
  const [fullName, setFullName] = useState("");
  const [isAdmin,  setIsAdmin]  = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (editingUser) {
      setUsername(editingUser.username || ""); setEmail(editingUser.email || "");
      setFullName(editingUser.full_name || ""); setIsAdmin(Boolean(editingUser.is_admin));
      setIsActive(editingUser.is_active !== false); setPassword("");
    } else {
      setUsername(""); setEmail(""); setFullName(""); setIsAdmin(false); setIsActive(true); setPassword("");
    }
  }, [editingUser, open]);

  const handleSubmit = () => {
    const base = { email: email.trim(), full_name: fullName || null, is_admin: isAdmin, is_active: isActive };
    const payload = isEdit ? { ...base, ...(password ? { password } : {}) }
                           : { ...base, username, password };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{isEdit ? t("admin.edit_user") : t("admin.add_user")}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isEdit && (
            <TextField label={t("admin.col_username")} size="small" fullWidth value={username}
              onChange={e => setUsername(e.target.value)} required sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          )}
          <TextField label={t("admin.col_email")} type="email" size="small" fullWidth value={email}
            onChange={e => setEmail(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField label={t("profile.full_name")} size="small" fullWidth value={fullName}
            onChange={e => setFullName(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField label={isEdit ? t("auth.password") + " (laisser vide pour ne pas changer)" : t("auth.password")}
            type="password" size="small" fullWidth value={password}
            onChange={e => setPassword(e.target.value)} required={!isEdit}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <Stack direction="row" spacing={2}>
            <FormControlLabel control={<Switch checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />} label="Admin" />
            <FormControlLabel control={<Switch checked={isActive} onChange={e => setIsActive(e.target.checked)} />} label={t("common.active")} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>{t("common.cancel")}</Button>
        <Button variant="contained" onClick={handleSubmit}
          sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
          {isEdit ? t("common.save") : t("common.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Dialog attribuer credits
function GrantCreditsDialog({ open, onClose, user, onSuccess }) {
  const [packs,        setPacks]        = useState([]);
  const [packId,       setPackId]       = useState("");
  const [minutes,      setMinutes]      = useState("");
  const [note,         setNote]         = useState("");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [currentCred,  setCurrentCred]  = useState(null);
  const [result,       setResult]       = useState(null);  // résultat après attribution

  useEffect(() => {
    if (!open) {
      setPackId(""); setMinutes(""); setNote(""); setError(""); setResult(null); setCurrentCred(null);
      return;
    }
    setLoadingPacks(true);
    Promise.all([
      api.get("/admin/packages/packs", { params: { limit: 200 } }),
      api.get(`/admin/users/${user?.id}/lab-credits`).catch(() => null),
    ])
      .then(([packsRes, credRes]) => {
        const items = packsRes.data?.items || packsRes.data || [];
        setPacks(items.filter(p => p.is_active));
        if (credRes) setCurrentCred(credRes.data?.summary || null);
      })
      .catch(() => setError("Impossible de charger les packs"))
      .finally(() => setLoadingPacks(false));
  }, [open, user?.id]);

  const selectedPack = packs.find(p => String(p.id) === String(packId));
  const effectiveMin = minutes && Number(minutes) > 0
    ? Number(minutes)
    : selectedPack?.total_minutes || 0;

  const handleSubmit = async () => {
    if (!packId) return setError("Sélectionne un pack");
    setLoading(true); setError(""); setResult(null);
    try {
      const payload = { pack_id: Number(packId) };
      if (minutes && Number(minutes) > 0) payload.minutes = Number(minutes);
      if (note.trim()) payload.note = note.trim();
      const { data } = await api.post(`/admin/users/${user.id}/grant-credits`, payload);
      setResult(data);
      onSuccess(`✅ ${data.minutes_added} min attribuées à ${user.username || user.email} (Wallet #${data.wallet_id})`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de l'attribution");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
        <CreditCardIcon sx={{ color: "#6366f1" }} /> Attribuer des crédits
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>

          {/* Utilisateur cible */}
          <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 2, border: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: "#6366f1" }}>
                  {String(user?.username || user?.email || "?")[0].toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={700}>{user?.username || "—"}</Typography>
                  <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                </Box>
              </Stack>
              {currentCred && (
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: "block" }}>
                    Solde actuel
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{
                    color: (currentCred.total_remaining || 0) < 30 ? "#ef4444" : "#22c55e"
                  }}>
                    {currentCred.total_remaining ?? "?"} min
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Sélection pack */}
          <FormControl size="small" fullWidth>
            <InputLabel>Pack de crédits</InputLabel>
            <Select value={packId} label="Pack de crédits"
              onChange={e => { setPackId(e.target.value); setMinutes(""); setResult(null); }}
              sx={{ borderRadius: 2 }} disabled={loadingPacks}>
              {loadingPacks
                ? <MenuItem value=""><CircularProgress size={14} sx={{ mr: 1 }} />Chargement…</MenuItem>
                : packs.map(p => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
                      <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={`${p.total_minutes} min`}
                          sx={{ height: 16, fontSize: 10, bgcolor: "#eef2ff", color: "#6366f1" }} />
                        {p.validity_days
                          ? <Typography variant="caption" color="text.secondary">{p.validity_days}j</Typography>
                          : <Typography variant="caption" color="text.secondary">∞</Typography>}
                      </Stack>
                    </Stack>
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>

          {/* Détail pack sélectionné + override minutes */}
          {selectedPack && (
            <Box sx={{ bgcolor: "#f0f9ff", borderRadius: 2, p: 2, border: "1px solid #bae6fd" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" fontWeight={700} sx={{ color: "#0369a1" }}>
                    {selectedPack.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 11 }}>
                    {selectedPack.total_minutes} min par défaut
                    {selectedPack.validity_days
                      ? ` · expire dans ${selectedPack.validity_days} jours`
                      : " · sans expiration"}
                  </Typography>
                </Box>
                <Chip size="small" label={`${effectiveMin} min`}
                  sx={{ bgcolor: "#6366f1", color: "#fff", fontWeight: 700, fontSize: 11 }} />
              </Stack>
              <TextField
                label="Minutes à attribuer (laisser vide = total du pack)"
                type="number" size="small" fullWidth
                value={minutes}
                onChange={e => { setMinutes(e.target.value); setResult(null); }}
                placeholder={String(selectedPack.total_minutes)}
                inputProps={{ min: 1, max: selectedPack.total_minutes * 10 }}
                sx={{ mt: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Box>
          )}

          {/* Note interne */}
          <TextField label="Note interne (optionnel)" size="small" fullWidth
            value={note} onChange={e => setNote(e.target.value)}
            placeholder="Ex: Compensation incident, offre commerciale, test…"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />

          {/* Résumé avant envoi */}
          {selectedPack && !result && (
            <Box sx={{ bgcolor: "#fffbeb", borderRadius: 2, p: 1.5,
              border: "1px solid #fde68a", fontSize: 12 }}>
              <Typography variant="caption" sx={{ color: "#92400e" }}>
                Confirmation : <strong>{effectiveMin} min</strong> du pack{" "}
                <strong>{selectedPack.name}</strong> seront attribuées à{" "}
                <strong>{user?.username || user?.email}</strong>
                {currentCred ? ` — nouveau solde estimé : ${(currentCred.total_remaining || 0) + effectiveMin} min` : ""}.
              </Typography>
            </Box>
          )}

          {/* Résultat succès */}
          {result && (
            <Box sx={{ bgcolor: "#f0fdf4", borderRadius: 2, p: 2,
              border: "1px solid #86efac" }}>
              <Stack spacing={0.75}>
                <Typography variant="body2" fontWeight={700} sx={{ color: "#16a34a" }}>
                  ✅ Attribution réussie
                </Typography>
                {[
                  ["Pack",           result.pack_code],
                  ["Minutes ajoutées", `${result.minutes_added} min`],
                  ["Nouveau solde",  `${result.new_remaining} min`],
                  ["Wallet",         `#${result.wallet_id}`],
                  ["Expire",         result.expires_at
                    ? new Date(result.expires_at).toLocaleDateString("fr")
                    : "Sans expiration"],
                ].map(([k, v]) => (
                  <Stack key={k} direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                    <Typography variant="caption" fontWeight={700}>{v}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ borderRadius: 2, fontSize: 12 }}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={result ? onClose : onClose} sx={{ borderRadius: 2 }} disabled={loading}>
          {result ? "Fermer" : "Annuler"}
        </Button>
        {!result && (
          <Button variant="contained" onClick={handleSubmit}
            disabled={loading || !packId}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CreditCardIcon />}
            sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
            {loading ? "Attribution…" : "Attribuer"}
          </Button>
        )}
        {result && (
          <Button variant="contained" onClick={() => {
            setResult(null); setPackId(""); setMinutes(""); setNote("");
          }}
            sx={{ borderRadius: 2, bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}>
            Nouvelle attribution
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}


// Dialog voir solde
function WalletDialog({ open, onClose, user }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    if (!open || !user?.id) return;
    setLoading(true); setError(""); setData(null);
    api.get(`/admin/users/${user.id}/lab-credits`)
      .then(({ data }) => setData(data))
      .catch(() => setError("Impossible de charger les credits"))
      .finally(() => setLoading(false));
  }, [open, user]);

  const walletStatus = (w) => {
    const _now = new Date();
    if (!w.is_active) return { label: "Inactif", color: "#94a3b8", bg: "#f8fafc" };
    // Utiliser le statut calculé par le backend si disponible
    if (w.status === "expired")   return { label: "Expiré",  color: "#ef4444", bg: "#fef2f2" };
    if (w.status === "exhausted") return { label: "Epuisé",  color: "#f59e0b", bg: "#fffbeb" };
    if (w.status === "inactive")  return { label: "Inactif", color: "#94a3b8", bg: "#f8fafc" };
    // Fallback : recalcul côté frontend
    if (w.expires_at && new Date(w.expires_at) <= _now) return { label: "Expiré", color: "#ef4444", bg: "#fef2f2" };
    if (Number(w.remaining_minutes) <= 0) return { label: "Epuisé", color: "#f59e0b", bg: "#fffbeb" };
    return { label: "Actif", color: "#22c55e", bg: "#f0fdf4" };
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <AccountBalanceWalletIcon sx={{ color: "#6366f1" }} />
        Solde de credits — {user?.username || user?.email}
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {loading && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>}
        {error   && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
        {data && (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              {[
                { label: "Total wallets",    val: data.summary?.total_wallets    ?? 0, color: "#6366f1" },
                { label: "Wallets actifs",   val: data.summary?.active_wallets   ?? 0, color: "#22c55e" },
                { label: "Min. totales",     val: data.summary?.total_credits    ?? 0, color: "#3b82f6" },
                { label: "Min. restantes",   val: data.summary?.total_remaining  ?? 0, color: "#f59e0b" },
              ].map(({ label, val, color }) => (
                <Box key={label} sx={{ flex: 1, bgcolor: "#f8fafc", borderRadius: 2, p: 2, border: "1px solid #e2e8f0", textAlign: "center" }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color }}>{val}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Box>
              ))}
            </Stack>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Wallets ({data.wallets?.length ?? 0})
              </Typography>
              {(!data.wallets || data.wallets.length === 0)
                ? <Typography variant="body2" color="text.secondary">Aucun wallet.</Typography>
                : (
                  <Stack spacing={1.5}>
                    {data.wallets.map(w => {
                      const st  = walletStatus(w);
                      const pct = w.total_minutes > 0 ? Math.round((w.remaining_minutes / w.total_minutes) * 100) : 0;
                      const barCol = (st.label === "Expiré" || st.label === "Epuisé" || st.label === "Inactif")
                        ? "#ef4444"
                        : pct > 50 ? "#22c55e" : pct > 20 ? "#f59e0b" : "#ef4444";
                      return (
                        <Box key={w.id} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, p: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" fontWeight={700}>{w.pack_code || `Wallet #${w.id}`}</Typography>
                                {w.pack_name && <Typography variant="caption" color="text.secondary">— {w.pack_name}</Typography>}
                                <Chip size="small" label={st.label} sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: st.bg, color: st.color }} />
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                ID #{w.id} · Créé le {w.created_at ? new Date(w.created_at).toLocaleDateString("fr") : "—"}
                                {w.expires_at && ` · Expire le ${new Date(w.expires_at).toLocaleDateString("fr")}`}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                              <Typography variant="body2" fontWeight={700} sx={{ color: st.color }}>{w.remaining_minutes} min</Typography>
                              <Typography variant="caption" color="text.secondary">/ {w.total_minutes} min</Typography>
                            </Box>
                          </Stack>
                          <Box sx={{ mt: 1.5 }}>
                            <LinearProgress variant="determinate" value={pct}
                              sx={{ height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
                                "& .MuiLinearProgress-bar": {
                                  bgcolor: barCol, borderRadius: 3 }}} />
                            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Utilisé : {w.used_minutes ?? (w.total_minutes - w.remaining_minutes)} min
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{pct}%</Typography>
                            </Stack>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )
              }
            </Box>
            {data.transactions?.length > 0 && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  Transactions récentes ({data.total})
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12, color: "text.secondary", py: 0.5 } }}>
                      <TableCell>Date</TableCell><TableCell>Type</TableCell>
                      <TableCell>Minutes</TableCell><TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.transactions.map(tx => (
                      <TableRow key={tx.id} sx={{ "& td": { fontSize: 12, py: 0.75 } }}>
                        <TableCell sx={{ color: "text.secondary" }}>
                          {tx.created_at ? new Date(tx.created_at).toLocaleString("fr") : "—"}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={tx.type || "—"} sx={{ height: 18, fontSize: 10,
                            bgcolor: String(tx.type).includes("grant") ? "#f0fdf4" : String(tx.type).includes("debit") ? "#fef2f2" : "#f8fafc",
                            color:   String(tx.type).includes("grant") ? "#166534" : String(tx.type).includes("debit") ? "#991b1b" : "#475569" }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: Number(tx.amount) >= 0 ? "#16a34a" : "#dc2626" }}>
                          {Number(tx.amount) >= 0 ? "+" : ""}{tx.amount} min
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary", maxWidth: 220 }}>
                          <Tooltip title={tx.description || ""}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", maxWidth: 200 }}>
                              {tx.description || tx.note || "—"}
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

// Page principale
export default function AdminUserManagementPage() {
  const { t } = useTranslation();
  const [users,      setUsers]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [search,     setSearch]     = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [adminsOnly, setAdminsOnly] = useState(false);
  const [page,       setPage]       = useState(0);
  const PAGE_SIZE = 20;
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [snack,      setSnack]      = useState(null);
  const [dialog,     setDialog]     = useState({ open: false, user: null });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [grantDialog,  setGrantDialog]  = useState({ open: false, user: null });
  const [walletDialog, setWalletDialog] = useState({ open: false, user: null });

  const fetchUsers = useCallback(async (overridePage) => {
    setLoading(true); setError("");
    try {
      const params = { skip: (overridePage ?? page) * PAGE_SIZE, limit: PAGE_SIZE };
      if (search.trim()) params.search   = search.trim();
      if (activeOnly)    params.is_active = true;
      if (adminsOnly)    params.is_admin  = true;
      const { data } = await api.get("/admin/users/", { params });
      setUsers(data.items || []); setTotal(data.total || 0);
    } catch { setError(t("errors.unknown")); }
    finally { setLoading(false); }
  }, [page, search, activeOnly, adminsOnly, t]);

  useEffect(() => { fetchUsers(); }, [page, fetchUsers]);

  const handleDelete = async (userId) => {
    if (!window.confirm(t("admin.confirm_delete") + " ?")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setSnack({ text: t("admin.user_deleted"), sev: "success" });
      const pages = Math.ceil((total - 1) / PAGE_SIZE);
      const np    = page >= pages ? Math.max(0, pages - 1) : page;
      setPage(np); setTimeout(() => fetchUsers(np), 50);
    } catch { setError(t("errors.delete_failed")); }
  };

  const handleToggle = async (userId) => {
    try { await api.post(`/admin/users/${userId}/toggle-active`); setTimeout(() => fetchUsers(), 50); }
    catch { setError(t("errors.unknown")); }
  };

  const handleResetPwd = async (userId) => {
    const pwd = window.prompt(t("admin.reset_password"));
    if (!pwd) return;
    setPwdLoading(true);
    try {
      await api.post(`/admin/users/${userId}/reset-password`, { password: pwd });
      setSnack({ text: t("admin.password_reset"), sev: "success" });
    } catch { setError(t("errors.unknown")); }
    finally { setPwdLoading(false); }
  };

  const handleSubmit = async (formData) => {
    try {
      if (dialog.user) await api.put(`/admin/users/${dialog.user.id}`, formData);
      else             await api.post("/admin/users/", formData);
      setDialog({ open: false, user: null });
      setSnack({ text: dialog.user ? t("admin.user_updated") : t("admin.user_created"), sev: "success" });
      setTimeout(() => fetchUsers(), 50);
    } catch (e) { setError(e?.response?.data?.detail || t("errors.unknown")); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box sx={pageWrap}>
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}
        justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t("admin.users_title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {total} {t("admin.users_total").toLowerCase()}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <TextField size="small" placeholder={t("admin.search")} value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { setPage(0); fetchUsers(0); } }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.disabled", fontSize: 18 }} /> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 }, minWidth: 200 }} />
          <FormControlLabel
            control={<Switch size="small" checked={activeOnly}
              onChange={e => { setActiveOnly(e.target.checked); setPage(0); setTimeout(() => fetchUsers(0), 50); }} />}
            label={<Typography variant="body2">{t("admin.active_only")}</Typography>} sx={{ mx: 0 }} />
          <FormControlLabel
            control={<Switch size="small" checked={adminsOnly}
              onChange={e => { setAdminsOnly(e.target.checked); setPage(0); setTimeout(() => fetchUsers(0), 50); }} />}
            label={<Typography variant="body2">{t("admin.admins_only")}</Typography>} sx={{ mx: 0 }} />
          <Tooltip title={t("common.refresh")}>
            <IconButton onClick={() => fetchUsers()} disabled={loading} size="small"
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => setDialog({ open: true, user: null })}
            sx={{ borderRadius: 2, ...btnPrimary }}>
            {t("admin.add_user")}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor: "#f8fafc" }}>
              {[t("admin.col_username"), t("admin.col_email"), t("profile.full_name"),
                t("admin.col_role"), t("admin.col_active"), t("admin.col_created_at"), t("admin.col_actions")].map((h, i) => (
                <Box component="th" key={i} sx={{ ...tableHeaderCell, p: 1.5, textAlign: i === 6 ? "right" : "left" }}>{h}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {users.length === 0 && !loading ? (
              <Box component="tr">
                <Box component="td" colSpan={7} sx={{ py: 6, textAlign: "center", color: "text.secondary", p: 3 }}>
                  {t("admin.no_user_found")}
                </Box>
              </Box>
            ) : users.map(u => (
              <Box component="tr" key={u.id} sx={{ ...tableRow, "& td": { p: 1.5, borderBottom: "1px solid #f1f5f9" } }}>
                <Box component="td">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: "#6366f1" }}>
                      {String(u.username || u.email || "?")[0].toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{u.username}</Typography>
                  </Stack>
                </Box>
                <Box component="td"><Typography variant="body2" color="text.secondary">{u.email || "—"}</Typography></Box>
                <Box component="td"><Typography variant="body2">{u.full_name || "—"}</Typography></Box>
                <Box component="td">
                  {u.role === "super_admin"
                    ? <Chip size="small" label="Super Admin" sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 700, height: 20, fontSize: 10 }} />
                    : u.role === "admin" || u.is_admin
                    ? <Chip size="small" label="Admin" sx={{ bgcolor: "#eef2ff", color: "#6366f1", fontWeight: 700, height: 20, fontSize: 10 }} />
                    : <Chip size="small" label="User"  sx={{ bgcolor: "#f8fafc", color: "#64748b", fontWeight: 700, height: 20, fontSize: 10 }} />
                  }
                </Box>
                <Box component="td">
                  <Switch size="small" checked={Boolean(u.is_active)} onChange={() => handleToggle(u.id)} />
                </Box>
                <Box component="td" sx={{ fontSize: 12, color: "text.secondary" }}>
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </Box>
                <Box component="td" sx={{ textAlign: "right" }}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Voir le solde de credits">
                      <IconButton size="small" onClick={() => setWalletDialog({ open: true, user: u })}
                        sx={{ "&:hover": { bgcolor: "#f0fdf4" }, color: "#16a34a" }}>
                        <AccountBalanceWalletIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Attribuer des credits">
                      <IconButton size="small" onClick={() => setGrantDialog({ open: true, user: u })}
                        sx={{ "&:hover": { bgcolor: "#eef2ff" }, color: "#6366f1" }}>
                        <CreditCardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.edit")}>
                      <IconButton size="small" onClick={() => setDialog({ open: true, user: u })}
                        sx={{ "&:hover": { bgcolor: "#eef2ff" } }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("admin.reset_password")}>
                      <span>
                        <IconButton size="small" onClick={() => handleResetPwd(u.id)} disabled={pwdLoading}
                          sx={{ "&:hover": { bgcolor: "#fffbeb" } }}>
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                      <IconButton size="small" color="error" onClick={() => handleDelete(u.id)}
                        sx={{ "&:hover": { bgcolor: "#fef2f2" } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ px: 3, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            {total} utilisateur{total !== 1 ? "s" : ""} — Page {page + 1} / {totalPages}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" disabled={page === 0 || loading}
              onClick={() => { const np = page - 1; setPage(np); setTimeout(() => fetchUsers(np), 50); }}
              sx={{ borderRadius: 2, fontSize: 12 }}>← {t("common.previous")}</Button>
            <Button size="small" variant="outlined" disabled={(page + 1) * PAGE_SIZE >= total || loading}
              onClick={() => { const np = page + 1; setPage(np); setTimeout(() => fetchUsers(np), 50); }}
              sx={{ borderRadius: 2, fontSize: 12 }}>{t("common.next")} →</Button>
          </Stack>
        </Stack>
      </Paper>

      <UserFormDialog open={dialog.open} onClose={() => setDialog({ open: false, user: null })}
        onSubmit={handleSubmit} editingUser={dialog.user} />

      <GrantCreditsDialog open={grantDialog.open} user={grantDialog.user || {}}
        onClose={() => setGrantDialog({ open: false, user: null })}
        onSuccess={msg => setSnack({ text: msg, sev: "success" })} />

      <WalletDialog open={walletDialog.open} user={walletDialog.user || {}}
        onClose={() => setWalletDialog({ open: false, user: null })} />

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack?.sev || "info"} onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
          {snack?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
